import { z } from 'zod';
import { RUN_ITEM_STATUSES, RUN_STATUSES } from '../enums.js';
import { MAX_LABELS_PER_CASE } from './label.js';
import { nonEmptyString, optionalText, paginationSchema, uuidSchema } from './common.js';
/** A run assembled from more than this is a release plan, not a run. */
export const MAX_CHECKLISTS_PER_RUN = 20;
export const createTestRunSchema = z.object({
    title: nonEmptyString(300, 'Title'),
    /** A run lives in a folder of the run tree; projects are created with one. */
    folderId: uuidSchema.describe('Folder'),
    labelIds: z.array(uuidSchema).max(MAX_LABELS_PER_CASE).default([]),
    description: optionalText(10_000),
    /** One run can cover several checklists; the order is the order given. */
    checklistIds: z
        .array(uuidSchema)
        .min(1, 'Select at least one checklist')
        .max(MAX_CHECKLISTS_PER_RUN),
    /** One of the project's environments (§24), no longer free text. */
    environmentId: uuidSchema.nullable().optional(),
    assigneeId: uuidSchema.nullable().optional(),
    comment: optionalText(5000),
    /** Start the linked autotests of its checks in GitLab; their results fill the checks. */
    runAutotests: z.boolean().optional(),
});
export const updateTestRunSchema = z
    .object({
    title: nonEmptyString(300, 'Title').optional(),
    folderId: uuidSchema.optional(),
    description: optionalText(10_000),
    environmentId: uuidSchema.nullable().optional(),
    assigneeId: uuidSchema.nullable().optional(),
    comment: optionalText(5000),
    /** When present, the whole label set is replaced. */
    labelIds: z.array(uuidSchema).max(MAX_LABELS_PER_CASE).optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
/**
 * Comments — the run's own and the one recorded with a result — carry the
 * same light Markdown the fields of a test case do, screenshot references
 * included, which is why they are not held to a two-line limit.
 */
export const setRunItemStatusSchema = z.object({
    status: z.enum(RUN_ITEM_STATUSES),
    comment: optionalText(5000),
});
export const bulkSetRunItemStatusSchema = z.object({
    itemIds: z.array(uuidSchema).min(1, 'Select at least one item').max(500),
    status: z.enum(RUN_ITEM_STATUSES),
    comment: optionalText(5000),
});
export const rerunSchema = z.object({
    title: nonEmptyString(300, 'Title').optional(),
    environmentId: uuidSchema.nullable().optional(),
    assigneeId: uuidSchema.nullable().optional(),
});
export const RUN_SORT_FIELDS = ['createdAt', 'updatedAt', 'title', 'seqNumber'];
export const listTestRunsSchema = paginationSchema.extend({
    folderId: uuidSchema.optional(),
    includeSubfolders: z.coerce.boolean().default(false),
    status: z.enum(RUN_STATUSES).optional(),
    assigneeId: uuidSchema.optional(),
    checklistId: uuidSchema.optional(),
    labelId: uuidSchema.optional(),
    q: z.string().trim().max(200).optional(),
    sort: z.enum(RUN_SORT_FIELDS).default('seqNumber'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
/** The checklists a run is about to be created from, comma-separated. */
export const runAutomationPreviewSchema = z.object({
    checklistIds: z
        .string()
        .transform((value) => value.split(',').filter(Boolean))
        .pipe(z.array(uuidSchema).min(1).max(MAX_CHECKLISTS_PER_RUN)),
    environmentId: uuidSchema.optional(),
});
//# sourceMappingURL=test-run.js.map